import React from 'react';

import { MRT_ColumnDef, MRT_TableOptions } from 'mantine-react-table';

import type { ListProps } from '@refinedev/mantine';
import { useDocumentTitle } from '@refinedev/react-router-v6';

import { useCreatePayor, useListPayors, useUpdatePayor } from 'api/resource';
import {
  CreateMutationDataParams,
  UpdateMutationDataParams,
  createMutationData,
  updateMutationData,
} from 'api/utils/MutationDataBuilder';
import { PayorColumns } from 'columns/Payor';
import {
  BillingDataGridWrapper,
  CreateModal,
  CreateRowButton,
  EditModal,
  EditRowButton,
  TableAlertBanner,
  TableProgressBar,
} from 'components/common';
import { useCanModify, useIsAdmin } from 'hooks';

import { Payor } from 'interfaces';
import { v4 } from 'uuid';

const normalizeText = (values: Payor): Payor => {
  return {
    ...values,
    fax: values.fax === '' ? null : values.fax,
    province: values.province === '' ? null : values.province,
    phone: values.phone === '' ? null : values.phone,
    email: values.email === '' ? null : values.email,
    note: values.note === '' ? null : values.note,
    billingNumberMinLength:
      typeof values.billingNumberMinLength === 'number'
        ? values.billingNumberMinLength
        : null,
    billingNumberMaxLength:
      typeof values.billingNumberMaxLength === 'number'
        ? values.billingNumberMaxLength
        : null,
    billingNumberFormat: values.billingNumberFormat || 'ANY',
  };
};

export const PayorManagement: React.FC<ListProps> = () => {
  const title = 'Payor Management';
  useDocumentTitle('Payors | QBilling');

  const isAdmin = useIsAdmin();
  const { canModify } = useCanModify('payors');

  const {
    data: payors = [] as Payor[],
    isLoading,
    isError,
    isFetching,
    error,
  } = useListPayors();

  const createPayor = useCreatePayor();
  const updatePayor = useUpdatePayor();

  const handleCreatePayor = async ({
    table,
    values,
  }: CreateMutationDataParams<Payor>) => {
    return createMutationData<Payor>({
      table,
      values: normalizeText({
        ...values,
        id: v4(),
      }),
      mutation: createPayor,
    });
  };

  const handleEditPayor = async ({
    row,
    table,
    values,
  }: UpdateMutationDataParams<Payor>) =>
    updateMutationData<Payor>({
      row,
      table,
      values: normalizeText(values),
      mutation: updatePayor,
    });

  const columns: MRT_ColumnDef<Payor>[] = PayorColumns();

  const tableOptions: MRT_TableOptions<Payor> = {
    data: payors,
    columns,
    state: {
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
    },
    createDisplayMode: 'modal',
    editDisplayMode: 'modal',
    enableFullScreenToggle: false,
    enableEditing: isAdmin,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: 'Edit',
        Cell: ({ row, table }) => (
          <EditRowButton
            row={row}
            table={table}
            type="payor"
            disabled={!canModify}
          />
        ),
        size: 100,
      },
    },
    onCreatingRowSave: handleCreatePayor,
    onEditingRowSave: handleEditPayor,
    mantineProgressProps({ isTopToolbar }) {
      return TableProgressBar({ isTopToolbar });
    },
    mantineToolbarAlertBannerProps() {
      return TableAlertBanner({ isError, message: error?.message });
    },
    renderTopToolbarCustomActions: ({ table }) => {
      return CreateRowButton<Payor>({
        type: 'Payor',
        table,
        disabled: !canModify,
      });
    },
    renderCreateRowModalContent: ({ table, row, internalEditComponents }) =>
      CreateModal<Payor>({
        table,
        row,
        internalEditComponents,
        type: 'payor',
      }),
    renderEditRowModalContent: ({ table, row, internalEditComponents }) =>
      EditModal<Payor>({
        table,
        row,
        internalEditComponents,
        type: 'payor',
      }),
  };

  return (
    <BillingDataGridWrapper
      title={title}
      tableOptions={tableOptions}
      dataTestID="payorManagement"
      showAlertBanner={false}
    />
  );
};
